import { useEffect, useRef, useState, type ReactNode, type TransitionEvent } from 'react';
import styles from './Popover.module.css';

/**
 * Where the panel sits relative to its anchor. `right-end` puts it beside the
 * anchor with their bottom edges flush — which is how the Figma sidebar footer
 * menu is positioned.
 */
export type PopoverPlacement =
  | 'right-end'
  | 'right-start'
  | 'top-start'
  | 'bottom-start'
  | 'bottom-end';

export type PopoverProps = {
  open: boolean;
  onClose: () => void;
  placement?: PopoverPlacement;
  /**
   * Whether the panel paints its own surface — white fill, 1px inset ring,
   * radius-md and shadow-lg. Turn it off when the content is itself a designed
   * surface, as `_Date picker menu` (123:2191) is, so the ring and shadow are
   * not painted twice.
   */
  surface?: boolean;
  /**
   * How the panel arrives. `slide` (the default) fades it in while it moves
   * `--space-4` out of its anchor. `scale` is the transitions.dev menu dropdown:
   * it grows from the anchor's corner, 0.97 → 1 on the way in and 1 → 0.99 on
   * the way out, with the corner taken from `placement`.
   */
  motion?: 'slide' | 'scale';
  /** Accessible name for the panel. */
  label?: string;
  /**
   * ARIA role for the panel. `menu` suits a list of commands, which is what the
   * sidebar footer menu is. A panel of checkboxes or form controls is not a
   * menu — `menu` expects `menuitem*` children — so those pass `group`.
   */
  role?: 'menu' | 'group' | 'dialog' | 'listbox';
  /**
   * `role="listbox"` only — announces that more than one row can be chosen at
   * once. Not in Figma, which has no concept of an accessible state; needed by
   * `Select`'s `multiple` mode, where without it a screen reader describes a
   * list of five ticked rows as a single-choice list.
   */
  multiselectable?: boolean;
  /** So a trigger can point `aria-controls` at the panel. */
  id?: string;
  className?: string;
  children?: ReactNode;
};

const placementClass: Record<PopoverPlacement, string> = {
  'right-end': styles.rightEnd,
  'right-start': styles.rightStart,
  'top-start': styles.topStart,
  'bottom-start': styles.bottomStart,
  'bottom-end': styles.bottomEnd,
};

/**
 * An anchored floating surface. Render it inside a positioned container (the
 * `anchor` class below) and it places itself against that container.
 *
 * Closes on Escape and on pointer-down outside itself — the behaviour every
 * menu, dropdown and drawer in this system needs, so it lives here rather than
 * in the sidebar.
 */
export function Popover({
  open,
  onClose,
  placement = 'right-end',
  surface = true,
  motion = 'slide',
  label,
  role = 'menu',
  multiselectable,
  id,
  className,
  children,
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  /*
   * The panel outlives `open` by one transition so it can animate out. Without
   * this it is unmounted on the same frame the prop flips and the exit never
   * gets to run — which is what made every menu in the system vanish rather
   * than close.
   *
   * Adjusting state during render (rather than in an effect) is deliberate: an
   * effect would paint one frame of the panel in its closed styles before the
   * class flips, which flickers. React re-runs this render before committing.
   */
  const [exiting, setExiting] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    setExiting(!open);
  }

  /*
   * Fires once per property, and bubbles from anything inside the panel, so
   * check both. Reopening mid-exit sets `exiting` false above and the CSS
   * retargets, so a late event from the abandoned exit cannot unmount the panel
   * that is now opening.
   */
  /*
   * Exit fallback (deepdive-v3, mirrors Dialog's guard). If nothing transitions —
   * closed before the entrance started, reduced motion collapsing durations, or
   * an environment without transitions — no `transitionend` arrives and the
   * panel would stay mounted. One frame after closing, a panel with nothing
   * running is done.
   */
  useEffect(() => {
    if (!exiting) return;
    const frame = requestAnimationFrame(() => {
      const panel = panelRef.current;
      const running =
        panel && typeof panel.getAnimations === 'function' ? panel.getAnimations().length : 0;
      if (running === 0) setExiting(false);
    });
    return () => cancelAnimationFrame(frame);
  }, [exiting]);

  const onTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (!open && event.target === panelRef.current && event.propertyName === 'opacity') {
      setExiting(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      /*
       * Consume the key (deepdive-v3). Inside a native `<dialog>` Escape's
       * default action is the dialog's close request, so an open popover in a
       * modal used to close the modal too. Same fix `Select` already carries.
       * Default actions run after propagation, so a document listener is in time.
       */
      event.preventDefault();
      onClose();
    };
    const onPointerDown = (event: PointerEvent) => {
      const panel = panelRef.current;
      if (!panel) return;
      const target = event.target as Node | null;
      // Ignore clicks on the anchor itself — the trigger handles its own toggle.
      if (target && !panel.contains(target) && !panel.parentElement?.contains(target)) onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, onClose]);

  if (!open && !exiting) return null;

  const classes = [
    styles.panel,
    open ? styles.open : null,
    surface ? styles.surface : null,
    motion === 'scale' ? styles.scale : null,
    placementClass[placement],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={panelRef}
      className={classes}
      id={id}
      role={role}
      aria-multiselectable={multiselectable}
      aria-label={label}
      /*
       * On the way out the panel is still painted but no longer part of the
       * page: `inert` takes it out of the accessibility tree and the tab order,
       * and pushes focus out if it was inside — which is what unmounting used
       * to do. `aria-hidden` would be wrong here, since it can leave focus on an
       * element hidden from assistive tech.
       */
      inert={!open}
      onTransitionEnd={onTransitionEnd}
    >
      {children}
    </div>
  );
}

/** Class to put on the element a Popover should anchor to. */
export const popoverAnchorClass = styles.anchor;
